export interface ProjectLaborCost {
  id: string; // Guid in C# becomes string in TypeScript
  
  // Foreign keys / navigation
  projectId: number;
  employeeId?: number; // Optional - null if subcontractor
  subcontractorId?: number; // Optional - null if in-house crew
  
  // Worker identification
  workerName: string;
  laborType: LaborType;
  
  // Classification (affects prevailing wage, burden rates, etc.)
  trade: TradeType;
  
  // Dates & Hours
  workDate: Date;
  startTime?: Date; // Optional
  endTime?: Date; // Optional
  
  regularHours: number;
  overtimeHours: number;
  doubleTimeHours: number;

  // Pay rates (base rates before burden)
  regularRate: number; // $/hr
  overtimeRate: number; // usually 1.5x
  doubleTimeRate: number; // usually 2.0x

  // Flat / lump-sum rate (e.g. a subcontractor paid a fixed price for the job
  // instead of by the hour). When isFlatRate is true, hours & hourly rates are
  // ignored and burden is not applied.
  isFlatRate: boolean;
  flatRateAmount: number;  // Lump sum paid to the contractor (cost)
  flatBillAmount: number;  // Lump sum charged to the client (billing)

  // Calculated base pay (computed property)
  basePay: number;
  
  // Burden / Overhead (taxes, workers comp, insurance, benefits)
  burdenPercentage: number; // default 35% - typical 30-45% in construction
  burdenAmount: number; // computed property
  
  // Total labor cost (what you actually bill or record)
  totalCost: number; // computed property
  
  // Billing (if different from cost)
  billingRate: number; // What you charge the client per hour
  billedAmount: number; // computed property
  grossProfit: number; // computed property
  
  // Optional fields
  description: string;
  phaseOrTask: string; // e.g., "Framing", "Drywall", "Site Cleanup"
  isPrevailingWageJob: boolean;
  unionClassification?: string; // Optional
  
  isPaid: boolean;
  paidDate?: Date;

  createdAt: Date;
  updatedAt?: Date;
}

export enum LaborType {
  HourlyEmployee = 'HourlyEmployee',
  SalariedEmployee = 'SalariedEmployee',
  Subcontractor = 'Subcontractor',
  OwnerOperator = 'OwnerOperator'
}

export enum TradeType {
  GeneralLabor = 'GeneralLabor',
  Carpenter = 'Carpenter',
  Electrician = 'Electrician',
  Plumber = 'Plumber',
  HVAC = 'HVAC',
  Mason = 'Mason',
  Drywall = 'Drywall',
  Painter = 'Painter',
  Roofer = 'Roofer',
  ConcreteFinisher = 'ConcreteFinisher',
  EquipmentOperator = 'EquipmentOperator',
  Foreman = 'Foreman',
  Superintendent = 'Superintendent',
  ProjectManager = 'ProjectManager'
}

// Helper function to calculate computed properties
export class ProjectLaborCostCalculator {
  static calculateBasePay(laborCost: Partial<ProjectLaborCost>): number {
    if (laborCost.isFlatRate) {
      return laborCost.flatRateAmount || 0;
    }
    const regular = (laborCost.regularHours || 0) * (laborCost.regularRate || 0);
    const overtime = (laborCost.overtimeHours || 0) * (laborCost.overtimeRate || 0);
    const doubleTime = (laborCost.doubleTimeHours || 0) * (laborCost.doubleTimeRate || 0);
    return regular + overtime + doubleTime;
  }

  static calculateBurdenAmount(basePay: number, burdenPercentage: number): number {
    return basePay * (burdenPercentage / 100);
  }

  static calculateTotalCost(basePay: number, burdenAmount: number): number {
    return basePay + burdenAmount;
  }

  static calculateBilledAmount(laborCost: Partial<ProjectLaborCost>): number {
    if (laborCost.isFlatRate) {
      return laborCost.flatBillAmount || 0;
    }
    const totalHours = (laborCost.regularHours || 0) + (laborCost.overtimeHours || 0) + (laborCost.doubleTimeHours || 0);
    return totalHours * (laborCost.billingRate || 0);
  }

  static calculateGrossProfit(billedAmount: number, totalCost: number): number {
    return billedAmount - totalCost;
  }

  static updateCalculatedFields(laborCost: Partial<ProjectLaborCost>): ProjectLaborCost {
    const basePay = this.calculateBasePay(laborCost);
    // Flat-rate jobs are a lump sum - no burden is applied on top.
    const burdenAmount = laborCost.isFlatRate
      ? 0
      : this.calculateBurdenAmount(basePay, laborCost.burdenPercentage || 35);
    const totalCost = this.calculateTotalCost(basePay, burdenAmount);
    const billedAmount = this.calculateBilledAmount(laborCost);
    const grossProfit = this.calculateGrossProfit(billedAmount, totalCost);
    
    return {
      ...laborCost,
      basePay,
      burdenAmount,
      totalCost,
      billedAmount,
      grossProfit
    } as ProjectLaborCost;
  }
}